import { Button } from '@src/components/Button';
import { ButtonType } from '@src/types';
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { type InputOption } from '@src/components/base/OptionsForInput';
import { TextInputWithOptionsMulti } from '@src/components/base/TextInputWithOptionsMulti';
import { isEmail } from '@src/hooks/validation/rules/regex';
import { noDuplicates } from '@src/hooks/validation/rules/noDuplicate';
import { useTranslation } from 'react-i18next';
import { useValidation } from '@src/hooks/validation/useValidation';
import recruiterService, {
  type Recruiter,
} from '@src/api/services/RecruiterService';

type RecruiterOption = Recruiter & InputOption;

type RecruiterInviteInputProps = {
  setRecruiter?: Dispatch<SetStateAction<Recruiter[]>>;
  recruiter: Recruiter[];
  allRecruiter: Recruiter[];
  onRecruiterAdded?: (newRecruiter: Recruiter) => void;
  organizationId?: string;
};

export const RecruiterInviteInput = ({
  allRecruiter,
  recruiter,
  organizationId,
  setRecruiter,
  onRecruiterAdded,
}: RecruiterInviteInputProps) => {
  const { t } = useTranslation('recruiterView');

  const [value, setValue] = useState('');

  const options = useMemo(
    () =>
      allRecruiter
        .filter((r) => !recruiter.some((sel) => sel.id === r.id))
        .map((r) => ({ ...r, label: r.email })),
    [allRecruiter, recruiter],
  );

  const validation = useValidation(
    value,
    [
      isEmail,
      noDuplicates(
        recruiter,
        (value, item) => value.toLowerCase() === item.email.toLowerCase(),
      ),
    ],
    undefined,
  );

  const addRecruiterToRecruiters = useCallback(
    (recruiter: Recruiter) => {
      setRecruiter?.((prev) => {
        const copy = [...prev];
        copy.push(recruiter);
        return copy;
      });
      setValue('');
    },
    [setRecruiter],
  );

  const onOptionSelected = useCallback(
    (recruiter: RecruiterOption | InputOption) => {
      if (!('id' in recruiter)) {
        return;
      }
      const { label: _label, ...rest } = recruiter;
      addRecruiterToRecruiters(rest);
    },
    [addRecruiterToRecruiters],
  );

  const inviteRecruiter = useCallback(async () => {
    if (!organizationId) {
      return;
    }
    const newRecruiter = await recruiterService.addRecruiter(
      organizationId,
      value,
    );
    onRecruiterAdded?.(newRecruiter);
    addRecruiterToRecruiters(newRecruiter);
  }, [addRecruiterToRecruiters, organizationId, onRecruiterAdded, value]);

  const buttonDisabled = useMemo(() => value.length === 0, [value]);

  const onEnter = useCallback(
    (selectedFromOptions: boolean) => {
      if (selectedFromOptions || !validation.isValid || !value.trim().length) {
        return;
      }
      return inviteRecruiter();
    },
    [inviteRecruiter, validation.isValid, value],
  );

  return (
    <span className="flex w-full gap-2">
      <TextInputWithOptionsMulti<RecruiterOption>
        placeholder={t('inputPlaceholder')}
        showError={false}
        className="grow"
        autoFocus
        options={options}
        value={value}
        onChange={(value) => setValue(value.trim())}
        onOptionSelected={onOptionSelected}
        inputTestId="invite-recruiter-input"
        validation={validation}
        validationPosition="tooltip"
        disabled={!organizationId}
        onEnter={onEnter}
      ></TextInputWithOptionsMulti>
      <Button
        buttonType={ButtonType.PRIMARY}
        label={t('buttonLabel')}
        className="md:min-w-26"
        testId="invite-recruiter-button"
        disabled={buttonDisabled || !validation.isValid || !organizationId}
        onClick={inviteRecruiter}
      />
    </span>
  );
};
